import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { Router, RouterModule } from "@angular/router";

import { FormsModule } from "@angular/forms";

// Game Constants
const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 200;
const GROUND_Y = CANVAS_HEIGHT - 50;
const PLAYER_WIDTH = 40;
const PLAYER_HEIGHT = 40;
const PLAYER_X = 50;
const PLAYER_JUMP_STRENGTH = 16;
const GRAVITY = 0.7;
const OBSTACLE_MIN_HEIGHT = 20;
const OBSTACLE_MAX_HEIGHT = 50;
const OBSTACLE_WIDTH = 20;
const OBSTACLE_MIN_GAP = 1600;
const OBSTACLE_MAX_GAP = 3000;
const INITIAL_GAME_SPEED = 4;
const GAME_SPEED_INCREMENT = 0.0005;

class Player {
  x = PLAYER_X;
  y = GROUND_Y - PLAYER_HEIGHT;
  width = PLAYER_WIDTH;
  height = PLAYER_HEIGHT;
  dy = 0;
  isJumping = false;
  private blinkCounter = Math.floor(Math.random() * 180);
  private isBlinking = false;

  constructor(private ctx: CanvasRenderingContext2D) {}

  jump() {
    if (!this.isJumping) {
      this.isJumping = true;
      this.dy = -PLAYER_JUMP_STRENGTH;
    }
  }

  update() {
    if (this.isJumping) {
      this.dy += GRAVITY;
      this.y += this.dy;

      if (this.y > GROUND_Y - this.height) {
        this.y = GROUND_Y - this.height;
        this.isJumping = false;
        this.dy = 0;
      }
    } else {
      // Blinking logic when on the ground
      this.blinkCounter++;
      if (this.blinkCounter > 180) {
        this.isBlinking = true;
      }
      if (this.blinkCounter > 185) {
        this.isBlinking = false;
        this.blinkCounter = 0;
      }
    }
  }

  draw() {
    const x = this.x;
    const y = this.y;
    const width = this.width;
    const height = this.height;
    const radius = width / 2;

    // Body
    this.ctx.fillStyle = "#666";
    this.ctx.beginPath();
    this.ctx.arc(x + radius, y + radius, radius, 0, Math.PI * 2);
    this.ctx.fill();

    // Eyes
    if (this.isJumping) {
      // Jumping eyes (upward arcs)
      this.ctx.strokeStyle = "white";
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(x + radius - 8, y + radius, 4, Math.PI, 2 * Math.PI);
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.arc(x + radius + 8, y + radius, 4, Math.PI, 2 * Math.PI);
      this.ctx.stroke();
    } else if (this.isBlinking) {
      // Closed eyes (lines)
      this.ctx.strokeStyle = "white";
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(x + radius - 12, y + radius - 5);
      this.ctx.lineTo(x + radius - 4, y + radius - 5);
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(x + radius + 4, y + radius - 5);
      this.ctx.lineTo(x + radius + 12, y + radius - 5);
      this.ctx.stroke();
    } else {
      // Normal open eyes
      this.ctx.fillStyle = "white";
      this.ctx.beginPath();
      this.ctx.arc(x + radius - 8, y + radius - 5, 5, 0, Math.PI * 2);
      this.ctx.arc(x + radius + 8, y + radius - 5, 5, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = "black";
      this.ctx.beginPath();
      this.ctx.arc(x + radius - 8, y + radius - 5, 2, 0, Math.PI * 2);
      this.ctx.arc(x + radius + 8, y + radius - 5, 2, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }
}

class Obstacle {
  x: number;
  y: number;
  width = OBSTACLE_WIDTH;
  height: number;

  constructor(
    private ctx: CanvasRenderingContext2D,
    canvasWidth: number,
  ) {
    this.x = canvasWidth;
    this.height =
      Math.random() * (OBSTACLE_MAX_HEIGHT - OBSTACLE_MIN_HEIGHT) +
      OBSTACLE_MIN_HEIGHT;
    this.y = GROUND_Y - this.height;
  }

  update(gameSpeed: number) {
    this.x -= gameSpeed;
  }

  draw() {
    this.ctx.fillStyle = "#dc3545";
    this.ctx.fillRect(this.x, this.y, this.width, this.height);
  }
}

class Cloud {
  x: number;
  y: number;
  size: number;

  constructor(
    private ctx: CanvasRenderingContext2D,
    canvasWidth: number,
  ) {
    this.x = canvasWidth + Math.random() * canvasWidth;
    this.y = Math.random() * (GROUND_Y - 100);
    this.size = Math.random() * 20 + 10;
  }

  update(gameSpeed: number) {
    this.x -= gameSpeed * 0.5; // Slower than obstacles for parallax
  }

  draw() {
    this.ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
    this.ctx.beginPath();
    this.ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    this.ctx.fill();
  }
}

@Component({
    selector: "app-page-not-found-component",
    imports: [FormsModule, RouterModule],
    templateUrl: "./page-not-found-component.component.html",
    styleUrls: ["./page-not-found-component.component.css"]
})
export class PageNotFoundComponentComponent implements OnInit, OnDestroy {
  @ViewChild("gameCanvas", { static: true })
  gameCanvas: ElementRef<HTMLCanvasElement>;

  private ctx: CanvasRenderingContext2D;
  private player: Player;
  private obstacles: Obstacle[] = [];
  private clouds: Cloud[] = [];
  private score = 0;
  private highScore = 0;
  private gameSpeed = INITIAL_GAME_SPEED;
  private nextObstacleTime: number;
  private gameLoop: any;
  private isGameOver = false;
  private jumpSound: HTMLAudioElement;
  private gameOverSound: HTMLAudioElement;

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.ctx = this.gameCanvas.nativeElement.getContext("2d");
    this.gameCanvas.nativeElement.width = CANVAS_WIDTH;
    this.gameCanvas.nativeElement.height = CANVAS_HEIGHT;
    this.loadSounds();
    this.setupGame();
    this.startGameLoop();
  }

  loadSounds() {
    this.jumpSound = new Audio("assets/light_audio.mp3");
    this.jumpSound.volume = 0.1;
    this.gameOverSound = new Audio("assets/bell-v2.mp3");
    this.gameOverSound.volume = 0.1;
  }

  setupGame(): void {
    this.player = new Player(this.ctx);
    this.obstacles = [];
    this.clouds = [];
    this.score = 0;
    this.gameSpeed = INITIAL_GAME_SPEED;
    this.isGameOver = false;
    this.nextObstacleTime =
      Date.now() +
      Math.random() * (OBSTACLE_MAX_GAP - OBSTACLE_MIN_GAP) +
      OBSTACLE_MIN_GAP;
    document.getElementById("game-over").style.display = "none";
    this.updateScoreDisplay();
  }

  startGameLoop(): void {
    this.gameLoop = setInterval(() => {
      if (!this.isGameOver) {
        this.updateGame();
        this.drawGame();
      }
    }, 1000 / 60);
  }

  updateGame(): void {
    this.gameSpeed += GAME_SPEED_INCREMENT;
    this.player.update();

    // Update obstacles
    this.obstacles.forEach((obstacle) => obstacle.update(this.gameSpeed));
    this.obstacles = this.obstacles.filter(
      (obstacle) => obstacle.x + obstacle.width > 0,
    );

    if (Date.now() > this.nextObstacleTime) {
      this.obstacles.push(
        new Obstacle(this.ctx, this.gameCanvas.nativeElement.width),
      );
      this.nextObstacleTime =
        Date.now() +
        (Math.random() * (OBSTACLE_MAX_GAP - OBSTACLE_MIN_GAP) +
          OBSTACLE_MIN_GAP) /
          (this.gameSpeed / INITIAL_GAME_SPEED);
    }

    // Update clouds
    this.clouds.forEach((cloud) => cloud.update(this.gameSpeed));
    this.clouds = this.clouds.filter((cloud) => cloud.x + cloud.size > 0);
    if (Math.random() < 0.01) {
      this.clouds.push(
        new Cloud(this.ctx, this.gameCanvas.nativeElement.width),
      );
    }

    // Collision detection
    this.obstacles.forEach((obstacle) => {
      if (this.isColliding(this.player, obstacle)) {
        this.gameOver();
      }
    });

    this.score++;
    this.updateScoreDisplay();
  }

  isColliding(player: Player, obstacle: Obstacle): boolean {
    const padding = 15; // to make collision more fair
    return (
      player.x < obstacle.x + obstacle.width - padding &&
      player.x + player.width > obstacle.x + padding &&
      player.y < obstacle.y + obstacle.height &&
      player.y + player.height > obstacle.y
    );
  }

  drawGame(): void {
    this.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw clouds
    this.clouds.forEach((cloud) => cloud.draw());

    // Draw ground
    this.ctx.strokeStyle = "#333";
    this.ctx.beginPath();
    this.ctx.moveTo(0, GROUND_Y);
    this.ctx.lineTo(CANVAS_WIDTH, GROUND_Y);
    this.ctx.stroke();

    this.player.draw();
    this.obstacles.forEach((obstacle) => obstacle.draw());
  }

  gameOver(): void {
    this.isGameOver = true;
    if (this.score > this.highScore) {
      this.highScore = this.score;
    }
    document.getElementById("game-over-score").innerText =
      "Score: " + this.score;
    document.getElementById("high-score").innerText =
      "High Score: " + this.highScore;
    document.getElementById("game-over").style.display = "flex";
    this.gameOverSound.play();
    clearInterval(this.gameLoop);
  }

  restartGame(): void {
    this.setupGame();
    this.startGameLoop();
  }

  updateScoreDisplay() {
    document.getElementById("score").innerText = "Score: " + this.score;
  }

  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.code === "Space") {
      event.preventDefault();
      if (this.isGameOver) {
        this.restartGame();
      } else {
        this.player.jump();
        this.jumpSound.play();
      }
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.gameLoop);
  }

  public handleRestartClick(): void {
    this.restartGame();
  }
}
